import React from "react";
import { Sparkles, BadgeCheck, ListChecks, TrendingUp, ArrowRight, XCircle } from "lucide-react";
import { useRedirect } from "../../../Shared/Hooks/useRedirect ";
import {
  ImprovementCenter,
  ProfileIntelligence,
  ProfileQualitySignals,
} from "../Types/CandidateIntelligenceTypes";
import { Bar, Section, EmptyState } from "./Section";

interface ProfileIntelligenceSectionProps {
  profile: ProfileIntelligence;
  signals: ProfileQualitySignals;
  improvement: ImprovementCenter;
}

export const ProfileIntelligenceSection: React.FC<ProfileIntelligenceSectionProps> = ({
  profile,
  signals,
  improvement,
}) => {
  const redirect = useRedirect();

  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
      {/* Breakout / drill into sections */}
      <Section
        icon={<ListChecks size={17} />}
        title="Profile Intelligence"
        subtitle={`${profile.completedSections}/${profile.totalSections} sections complete · ${profile.missingFields} fields missing`}
        className="lg:col-span-1"
      >
        <div className="flex flex-col gap-3">
          {profile.sections.map((section) => (
            <button
              key={section.key}
              type="button"
              onClick={() => redirect("/candidate/edit-profile")}
              className="group rounded-xl border border-slate-800 bg-slate-900/60 p-3 text-left transition hover:border-cyan-500/40"
            >
              <div className="mb-1.5 flex items-baseline justify-between gap-2">
                <span className="text-sm font-bold text-slate-200">{section.label}</span>
                <span className="text-[11px] font-semibold tabular-nums text-slate-400">
                  {section.filledCount}/{section.totalCount}
                </span>
              </div>
              <Bar percent={section.percent} />
              <div className="mt-1.5 flex items-center justify-between">
                <span className="text-[11px] text-slate-500">{section.detail}</span>
                <ArrowRight size={13} className="text-cyan-500 opacity-0 transition group-hover:opacity-100" />
              </div>
            </button>
          ))}
        </div>
      </Section>

      {/* Explainable quality signals */}
      <Section
        icon={<BadgeCheck size={17} />}
        title="Profile Quality Signals"
        subtitle={`${signals.presentCount} of ${signals.totalSignals} signals present`}
        className="lg:col-span-1"
      >
        <ul className="flex flex-col gap-2">
          {signals.signals.map((signal) => (
            <li
              key={signal.id}
              className="flex items-start gap-2.5 rounded-xl border border-slate-800 bg-slate-900/60 px-3 py-2.5"
            >
              {signal.present ? (
                <BadgeCheck size={16} className="mt-0.5 shrink-0 text-emerald-400" />
              ) : (
                <XCircle size={16} className="mt-0.5 shrink-0 text-slate-600" />
              )}
              <div className="min-w-0">
                <p className={`text-sm font-semibold ${signal.present ? "text-slate-100" : "text-slate-500"}`}>
                  {signal.label}
                </p>
                {signal.present && signal.detail ? (
                  <p className="truncate text-xs text-slate-500">{signal.detail}</p>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      </Section>

      {/* Improvement center */}
      <Section
        icon={<TrendingUp size={17} />}
        title="Profile Improvement Center"
        subtitle="Actionable steps to increase recruiter interest"
        right={
          <span className="rounded-full bg-cyan-500/15 px-2.5 py-1 text-[11px] font-bold text-cyan-300">
            {improvement.itemCount}
          </span>
        }
        className="lg:col-span-1"
      >
        {improvement.items.length === 0 ? (
          <EmptyState label="Your profile is fully optimized" />
        ) : (
          <ul className="flex flex-col gap-2">
            {improvement.items.slice(0, 6).map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => redirect(item.path)}
                  className="group w-full rounded-xl border border-slate-800 bg-slate-900/60 px-3 py-2.5 text-left transition hover:border-amber-400/40"
                >
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-slate-200 group-hover:text-amber-200">{item.action}</p>
                    <span
                      className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-wide ${
                        item.priority === "HIGH"
                          ? "bg-rose-500/15 text-rose-300"
                          : item.priority === "MEDIUM"
                            ? "bg-amber-500/15 text-amber-300"
                            : "bg-slate-700/60 text-slate-400"
                      }`}
                    >
                      {item.priority}
                    </span>
                  </div>
                  <p className="mt-0.5 text-xs text-slate-500">{item.reason}</p>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
};